/**
 * /ndt-inspection/dosimetry — dose records per worker per period
 * (GET /ndt-inspection/dosimetry).
 *
 * DOSE IS PERSONAL DATA. Office roles (admin, office_staff, supervisor) are
 * answered every worker's records; anyone else — a field worker — is answered
 * their own only, whatever they ask for. The worker filter is offered to office
 * roles only, and the page says which view it is showing.
 *
 * THE INVESTIGATION FLAG IS THE API'S: a deterministic comparison of the deep
 * dose with the company's investigation level. `null` means no level is set,
 * and renders "No level set" — never a guessed "Below".
 *
 * Doses arrive in the tenant's unit (`meta.units.dose`, mSv or mrem). Each row
 * opens an edit dialog for admins; there is no separate detail page, because a
 * dose record has nothing beyond its row.
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Plus, Users } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { ndtPersonName, useDeleteNdtDose, useNdtDoses, useNdtPeople } from '../../hooks/useNdtInspection';
import { queryLoadFailure } from '../../lib/load-failure';
import { canAdminNdt, formatNdtValue, ndtDosimeterLabel, ndtErrorMessage, seesAllNdtDoses, type NdtSkin } from '../../lib/ndt-inspection';
import type { NdtDose } from '../../types/ndt-inspection';
import { ConfirmDialog, DoseDialog } from './NdtDialogs';
import { NDT_INPUT, NdtBadge, NdtEmpty, NdtPanel, NdtRegister, ndtDay, type NdtColumn } from './NdtBits';

const FLAG_SKIN: Record<'over' | 'under' | 'unset', NdtSkin> = {
  over: { label: 'Investigate', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  under: { label: 'Below level', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  unset: { label: 'No level set', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' },
};
export const doseFlagSkin = (d: Pick<NdtDose, 'exceedsInvestigationLevel'>) =>
  d.exceedsInvestigationLevel === true ? FLAG_SKIN.over : d.exceedsInvestigationLevel === false ? FLAG_SKIN.under : FLAG_SKIN.unset;

export default function DosimetryScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const office = seesAllNdtDoses(user?.role);
  const userId = office ? searchParams.get('user') ?? '' : '';
  const canWrite = canAdminNdt(user?.role);
  const [editing, setEditing] = useState<NdtDose | null>(null);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<NdtDose | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const remove = useDeleteNdtDose();

  const listQuery = useNdtDoses(userId ? { userId } : undefined);
  const people = useNdtPeople(office);
  const doses = listQuery.data?.data ?? [];
  const units = listQuery.data?.meta.units ?? null;
  const loaded = !listQuery.isLoading && !queryLoadFailure(listQuery);
  const countOf = (pick: (d: NdtDose) => boolean) => displayValue(loaded ? doses.filter(pick).length : undefined);

  const setUser = (next: string) => {
    const p = new URLSearchParams(searchParams);
    if (next) p.set('user', next);
    else p.delete('user');
    setSearchParams(p, { replace: true });
  };

  const columns: NdtColumn<NdtDose>[] = [
    { key: 'worker', header: 'Worker', render: (d) => <span className="font-extrabold text-white">{d.userName || '—'}</span> },
    { key: 'period', header: 'Period', render: (d) => `${ndtDay(d.periodStart)} – ${ndtDay(d.periodEnd)}` },
    {
      key: 'dosimeter',
      header: 'Dosimeter',
      render: (d) => (
        <>
          {ndtDosimeterLabel(d.dosimeterKind)}
          {d.dosimeterSerial && <p className="mt-0.5 font-mono text-xs text-fieldiq-muted">{d.dosimeterSerial}</p>}
        </>
      ),
    },
    { key: 'deep', header: 'Deep dose', align: 'right', render: (d) => formatNdtValue(d.deepDose, units?.dose) },
    { key: 'shallow', header: 'Shallow dose', align: 'right', render: (d) => formatNdtValue(d.shallowDose, units?.dose) },
    { key: 'flag', header: 'Investigation', render: (d) => <NdtBadge skin={doseFlagSkin(d)} value={String(d.exceedsInvestigationLevel)} /> },
  ];

  return (
    <div data-testid="ndt-dosimetry-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="NDT inspection"
        title="Dosimetry"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canWrite && units ? (
            <button type="button" onClick={() => setAdding(true)} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              Add dose record
            </button>
          ) : undefined
        }
        stats={[
          { icon: <Users className="size-4" aria-hidden="true" />, label: 'Records', sub: office ? (userId ? 'One worker' : 'All workers') : 'Your own', tone: 'neutral', loading: listQuery.isLoading, value: countOf(() => true) },
          { icon: <AlertTriangle className="size-4" aria-hidden="true" />, label: 'Over level', sub: 'Investigate', tone: 'accent', loading: listQuery.isLoading, value: countOf((d) => d.exceedsInvestigationLevel === true) },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {office ? (
            <label className="block sm:max-w-[24rem]">
              <span className="sr-only">Worker</span>
              <select className={NDT_INPUT} value={userId} onChange={(e) => setUser(e.target.value)} aria-label="Worker">
                <option value="">All workers</option>
                {(people.data ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {ndtPersonName(u)}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <p className="text-xs text-fieldiq-muted" data-testid="ndt-dose-own">Showing your own dose records.</p>
          )}

          <NdtPanel
            eyebrow="Dose register"
            title="Dose records"
            testId="ndt-dose-register"
            footer={loaded && doses.length > 0 ? `${doses.length} record${doses.length === 1 ? '' : 's'}, newest period first` : undefined}
          >
            <NdtRegister
              query={listQuery}
              rows={doses}
              columns={columns}
              rail={(d) => doseFlagSkin(d).rail}
              onOpen={canWrite && units ? (d) => setEditing(d) : undefined}
              subject="dose records"
              testId="ndt-dose"
              minWidth="min-w-[860px]"
              empty={<NdtEmpty>{office ? 'No dose records are on file.' : 'You have no dose records on file.'}</NdtEmpty>}
              card={(d) => (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-extrabold text-white">{d.userName || '—'}</p>
                      <p className="mt-0.5 text-xs text-fieldiq-muted">
                        {ndtDay(d.periodStart)} – {ndtDay(d.periodEnd)}
                      </p>
                    </div>
                    <NdtBadge skin={doseFlagSkin(d)} value={String(d.exceedsInvestigationLevel)} />
                  </div>
                  <p className="mt-2 text-xs text-fieldiq-text">Deep {formatNdtValue(d.deepDose, units?.dose)}</p>
                </>
              )}
            />
          </NdtPanel>
        </div>
      </section>

      {canWrite && units && <DoseDialog open={adding} onClose={() => setAdding(false)} units={units} />}
      {canWrite && units && editing && (
        <DoseDialog
          open={!!editing}
          onClose={() => setEditing(null)}
          dose={editing}
          units={units}
          onDelete={() => {
            setDeleteError(null);
            setDeleting(editing);
            setEditing(null);
          }}
        />
      )}
      {canWrite && deleting && (
        <ConfirmDialog
          open={!!deleting}
          title="Delete dose record"
          body={`Remove ${deleting.userName || 'this worker'}'s dose record for ${ndtDay(deleting.periodStart)} – ${ndtDay(deleting.periodEnd)}?`}
          confirmLabel="Delete"
          pending={remove.isPending}
          error={deleteError}
          onClose={() => setDeleting(null)}
          onConfirm={() =>
            remove.mutate(deleting.id, {
              onSuccess: () => {
                toast.success('Dose record deleted');
                setDeleting(null);
              },
              onError: (err) => setDeleteError(ndtErrorMessage(err, 'The dose record could not be deleted.')),
            })
          }
        />
      )}
    </div>
  );
}
